import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useTheme,
} from "@mui/material";
import Warning from "@mui/icons-material/Warning";

export interface WarningDetail {
  label: string;
  value: string | number | null | undefined;
}

interface WarningModalProps {
  open: boolean;
  title: string;
  message: string;
  details?: WarningDetail[];
  detailsTitle?: string;
  guidanceMessage?: string;
  onCancel: () => void;
  onProceed: () => void;
  cancelText?: string;
  proceedText?: string;
  proceedColor?: "primary" | "secondary";
}

export const WarningModal = ({
  open,
  title,
  message,
  details,
  detailsTitle = "Warning Details:",
  guidanceMessage,
  onCancel,
  onProceed,
  cancelText = "CANCEL",
  proceedText = "PROCEED",
  proceedColor = "primary",
}: WarningModalProps) => {
  const theme = useTheme();

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Warning
            sx={{
              color: "warning.main",
              fontSize: "3rem",
              mr: 1.5,
            }}
          />
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, fontSize: "1.75rem" }}
          >
            {title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" paragraph sx={{ fontSize: "1.25rem" }}>
          {message}
        </Typography>

        {details && details.length > 0 ? (
          <Box p={2} mb={2}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, fontSize: "1.1rem" }}
            >
              {detailsTitle}
            </Typography>
            {details.map((detail, index) => (
              <Typography
                key={index}
                variant="body2"
                sx={{ fontSize: "1.1rem" }}
              >
                • {detail.label}: {detail.value ?? "N/A"}
              </Typography>
            ))}
          </Box>
        ) : null}

        {guidanceMessage ? (
          <Typography variant="body1" paragraph sx={{ fontSize: "1.25rem" }}>
            {guidanceMessage}
          </Typography>
        ) : null}
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px", gap: "12px" }}>
        <Button
          onClick={onCancel}
          variant="contained"
          sx={{
            minWidth: 140,
            fontSize: "1.1rem",
            padding: "10px 24px",
            backgroundColor: (theme) => theme.palette.charges.status.inactive,
            color: "white",
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onProceed}
          variant="contained"
          color={proceedColor}
          sx={{ minWidth: 140, fontSize: "1.1rem", padding: "10px 24px" }}
        >
          {proceedText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
